import React, { useState, useEffect, useCallback } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { visuallyHidden } from '@mui/utils';

const formatDate = (timestamp) => {
  if (!timestamp || typeof timestamp.toDate !== 'function') {
    return 'N/A';
  }
  return timestamp.toDate().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' });
};

function descendingComparator(a, b, orderBy) {
  let valA = a[orderBy];
  let valB = b[orderBy];

  if (orderBy === 'createdAt' && a.createdAt && b.createdAt) {
    valA = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
    valB = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
  } else if (orderBy === 'moderationScore' && a.moderationDetails && b.moderationDetails) {
    valA = a.moderationDetails.score;
    valB = b.moderationDetails.score;
  }

  if (valB < valA) return -1;
  if (valB > valA) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  { id: 'title', numeric: false, label: 'Pin Başlığı' },
  { id: 'id', numeric: false, label: 'Pin ID' },
  { id: 'moderationScore', numeric: true, label: 'AI Negatiflik Skoru' },
  { id: 'pinOwnerName', numeric: false, label: 'Pin Sahibi' },
  { id: 'createdAt', numeric: false, label: 'Oluşturulma Tarihi' },
  { id: 'actions', numeric: false, label: 'Eylemler', sortable: false },
];

const ModerationQueuePage = () => {
  const [pinsToReview, setPinsToReview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('moderationScore');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const fetchPinsToReview = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch pins that need review
      const pinsQuery = query(collection(db, 'pins'), where('moderationStatus', '==', 'review_required'));
      const pinsSnapshot = await getDocs(pinsQuery);
      const pins = pinsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      if (pins.length === 0) {
        setPinsToReview([]);
        setLoading(false);
        return;
      }

      // 2. Get unique user IDs from these pins
      const userIds = [...new Set(pins.map(p => p.userId).filter(Boolean))];

      // 3. Fetch user data for these IDs
      const usersMap = {};
      if (userIds.length > 0) {
        const usersQuery = query(collection(db, 'users'), where('uid', 'in', userIds));
        const usersSnapshot = await getDocs(usersQuery);
        usersSnapshot.forEach(doc => {
          usersMap[doc.id] = doc.data().displayName || doc.data().email || 'Bilinmeyen Kullanıcı';
        });
      }

      // 4. Combine pin data with user data
      const pinsWithOwners = pins.map(pin => ({
        ...pin,
        pinOwnerName: usersMap[pin.userId] || 'Bilinmiyor'
      }));

      setPinsToReview(pinsWithOwners);

    } catch (err) {
      setError(err.message);
      setSnackbar({ open: true, message: `Hata: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPinsToReview();
  }, [fetchPinsToReview]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleAction = async (pinId, action) => {
    const pinRef = doc(db, 'pins', pinId);
    try {
      if (action === 'approve') {
        await updateDoc(pinRef, { moderationStatus: 'approved' });
        setSnackbar({ open: true, message: 'Pin onaylandı.', severity: 'success' });
      } else if (action === 'quarantine') {
        await updateDoc(pinRef, { 
          moderationStatus: 'quarantined_manual',
          isPubliclyVisible: false 
        });
        setSnackbar({ open: true, message: 'Pin karantinaya alındı.', severity: 'warning' });
      }
      setPinsToReview(prevPins => prevPins.filter(p => p.id !== pinId));
    } catch (err) {
      setSnackbar({ open: true, message: `Hata: ${err.message}`, severity: 'error' });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };


  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>İçerik Denetim Sırası</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        AI tarafından incelenmesi için işaretlenen pinler. Negatiflik skoru -1.0'a ne kadar yakınsa, o kadar sakıncalı olabilir.
      </Typography>
      <Paper sx={{ width: '100%', mb: 2, mt: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell key={headCell.id} sortDirection={orderBy === headCell.id ? order : false}>
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                      {orderBy === headCell.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {stableSort(pinsToReview, getComparator(order, orderBy)).map((pin) => (
                <TableRow key={pin.id}>
                  <TableCell>{pin.title}</TableCell>
                  <TableCell>{pin.id}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={pin.moderationDetails?.score?.toFixed(2) || 'N/A'}
                      color={pin.moderationDetails?.score < -0.7 ? 'error' : 'warning'}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{pin.pinOwnerName || 'Bilinmiyor'}</TableCell>
                  <TableCell>{formatDate(pin.createdAt)}</TableCell>
                  <TableCell>
                    <Button size="small" color="success" onClick={() => handleAction(pin.id, 'approve')}>Onayla</Button>
                    <Button size="small" color="warning" onClick={() => handleAction(pin.id, 'quarantine')}>Karantinaya Al</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ModerationQueuePage;
